import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, User, Download, X } from "lucide-react";
import { publicApi, mediaUrl, mediaSrcSet } from "../lib/api";
import Seo, { SITE_NAME, breadcrumbSchema, logoUrl } from "../components/Seo";
import ShareRow from "../components/ShareRow";
import GuideDownloadModal from "../components/GuideDownloadModal";
import { RichContent } from "../lib/richText";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");

const LINK_CLS = "text-orange font-semibold underline underline-offset-2 hover:text-orange-dark transition-colors";
const LINK_RE = /(\[[^\]]+\]\([^)]+\))|(https?:\/\/[^\s]+)|((?<![\w])\/[a-zA-Z][a-zA-Z0-9\-/]*)/g;

const Anchor = ({ href, label, k }) => {
  const isInternal = href.startsWith("/");
  if (isInternal) return <Link key={k} to={href} className={LINK_CLS} data-testid="blog-inline-link">{label}</Link>;
  return <a key={k} href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLS} data-testid="blog-inline-link">{label}</a>;
};

// Turns plain text into React nodes with clickable links (markdown links, bare URLs, internal paths).
const renderLinks = (text, keyBase = "") => {
  const parts = [];
  let last = 0, m, i = 0;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const [full, md, url, path] = m;
    if (md) {
      const mm = /\[([^\]]+)\]\(([^)]+)\)/.exec(md);
      parts.push(<Anchor key={`${keyBase}a${i++}`} href={mm[2]} label={mm[1]} k={`${keyBase}a${i}`} />);
    } else if (url) {
      const trail = (url.match(/[.,;:!?)]+$/) || [""])[0];
      const clean = trail ? url.slice(0, -trail.length) : url;
      parts.push(<Anchor key={`${keyBase}u${i++}`} href={clean} label={clean} k={`${keyBase}u${i}`} />);
      if (trail) parts.push(trail);
    } else if (path) {
      parts.push(<Anchor key={`${keyBase}p${i++}`} href={path} label={path} k={`${keyBase}p${i}`} />);
    }
    last = m.index + full.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

// Inline formatting: **bold** on top of link parsing.
const renderInline = (text) => {
  const out = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(...renderLinks(text.slice(last, m.index), `n${i}`));
    out.push(<strong key={`b${i++}`} className="font-bold text-navy">{renderLinks(m[1], `b${i}`)}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(...renderLinks(text.slice(last), `n${i}`));
  return out;
};

// Renders a content line as a heading (## / ###) or a paragraph, with inline formatting.
const renderBlock = (line, key) => {
  const h3 = /^###\s+(.*)$/.exec(line);
  if (h3) return <h3 key={key} data-testid="blog-h3" className="font-display font-bold text-xl md:text-2xl text-navy mt-6 sm:mt-8 mb-2 sm:mb-3">{renderInline(h3[1])}</h3>;
  const h2 = /^##?\s+(.*)$/.exec(line);
  if (h2) return <h2 key={key} data-testid="blog-h2" className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-8 sm:mt-10 mb-3 sm:mb-4">{renderInline(h2[1])}</h2>;
  return <p key={key} className="text-slate-600 leading-relaxed text-base sm:text-lg mb-5 sm:mb-6">{renderInline(line)}</p>;
};

const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+\.\s+(.*)$/;
const QUOTE_RE = /^\s*>\s+(.*)$/;

// Renders an array of content lines, grouping consecutive bullet/numbered lines into lists and > lines into pull quotes.
const renderContent = (lines) => {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const isOrdered = ORDERED_RE.test(lines[i]);
    const isBullet = BULLET_RE.test(lines[i]);
    const isQuote = QUOTE_RE.test(lines[i]);
    if (isQuote) {
      const parts = [];
      const start = i;
      while (i < lines.length && QUOTE_RE.test(lines[i])) { parts.push(QUOTE_RE.exec(lines[i])[1]); i++; }
      blocks.push(
        <blockquote key={`q${start}`} data-testid="blog-pullquote" className="my-10 border-l-4 border-orange pl-6 md:pl-8 py-2">
          <p className="font-display font-bold text-2xl md:text-3xl text-navy leading-snug">{renderInline(parts.join(" "))}</p>
        </blockquote>
      );
    } else if (isOrdered || isBullet) {
      const re = isOrdered ? ORDERED_RE : BULLET_RE;
      const items = [];
      const start = i;
      while (i < lines.length && re.test(lines[i])) { items.push(re.exec(lines[i])[1]); i++; }
      const Tag = isOrdered ? "ol" : "ul";
      blocks.push(
        <Tag key={`list${start}`} data-testid={isOrdered ? "blog-ol" : "blog-ul"} className={`${isOrdered ? "list-decimal" : "list-disc"} pl-6 mb-5 sm:mb-6 space-y-2 text-slate-600 text-base sm:text-lg marker:text-orange marker:font-bold`}>
          {items.map((it, j) => <li key={j} className="leading-relaxed pl-1">{renderInline(it)}</li>)}
        </Tag>
      );
    } else {
      blocks.push(renderBlock(lines[i], `blk${i}`));
      i++;
    }
  }
  return blocks;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [guides, setGuides] = useState([]);
  const [activeGuide, setActiveGuide] = useState(null);
  const [ctaDismissed, setCtaDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setCtaDismissed(false);
    publicApi.blogPost(slug).then(setPost).catch(() => setPost(null)).finally(() => setLoading(false));
    publicApi.blog().then(setAllPosts).catch(() => {});
    publicApi.guides().then(setGuides).catch(() => {});
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-slate-400">Loading...</div>;

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Seo title="Post not found" noindex />
        <h1 className="font-display font-extrabold text-3xl text-navy mb-4">Post not found</h1>
        <Link to="/blog" className="text-orange font-bold inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to blog</Link>
      </div>
    );
  }

  const others = allPosts.filter((p) => p.slug !== slug);
  const sameCat = others.filter((p) => p.category === post.category);
  const more = [...sameCat, ...others.filter((p) => p.category !== post.category)].slice(0, 3);
  const matchGuide =
    guides.find((g) => g.id === post.guide_id && g.file_url) ||
    (post.guide_id ? guides.find((g) => g.id === post.guide_id) : null) ||
    guides.find((g) => g.file_url) || null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt || "",
    ...(post.image ? { image: post.image } : {}),
    ...(post.date ? { datePublished: post.date, dateModified: post.updated_at || post.date } : {}),
    mainEntityOfPage: { "@type": "WebPage", "@id": typeof window !== "undefined" ? window.location.href : "" },
    author: { "@type": "Organization", name: post.author || SITE_NAME },
    publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: logoUrl() } },
    articleSection: post.category,
  };
  const breadcrumb = breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: post.title, path: `/blog/${slug}` },
  ]);

  return (
    <div>
      <Seo
        title={post.title}
        description={post.excerpt || (post.content && post.content[0])}
        image={post.image}
        type="article"
        jsonLd={[articleSchema, breadcrumb]}
      />
      <section className="numbers-pattern text-white">
        <div className="relative max-w-3xl mx-auto px-6 py-20 md:py-24">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-300 hover:text-orange text-sm font-semibold mb-6 transition-colors"><ArrowLeft size={16} /> Back to blog</Link>
          <span className="inline-block bg-orange text-white text-xs font-bold px-3 py-1 rounded-full mb-4">{post.category}</span>
          <h1 className="font-display font-extrabold text-3xl md:text-5xl leading-tight">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-5 mt-6 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><User size={14} /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {post.readTime}</span>
          </div>
        </div>
      </section>

      <article className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          {post.youtube_id ? (
            <div data-testid="blog-youtube-embed" className="aspect-video rounded-2xl overflow-hidden bg-black mb-10 shadow-lg">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${post.youtube_id}`}
                title={post.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          ) : post.image ? (
            <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-white mb-10"><img loading="lazy" decoding="async" src={mediaUrl(post.image, 1080)} srcSet={mediaSrcSet(post.image)} sizes="(max-width: 767px) 100vw, 720px" alt={post.image_alt || post.title} className="w-full h-full object-contain" /></div>
          ) : null}
          <div className="max-w-none">
            {post.content_html ? <RichContent text={post.content_html} /> : renderContent(post.content || [])}
          </div>
          <ShareRow className="mt-10" path={`/blog/${post.slug}`} title={post.title} label="Share this post" />

          <div className="mt-8 bg-navy rounded-2xl p-8 text-white text-center">
            <h3 className="font-display font-bold text-2xl mb-3">Need help with this?</h3>
            <p className="text-slate-300 mb-6">Every business is different — tell us about yours and we'll recommend the support that's right for you.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all">Let's Talk <ArrowRight size={18} /></Link>
          </div>
        </div>
      </article>

      {more.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="font-display font-extrabold text-2xl text-navy mb-8">More from the blog</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {more.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                  <div className="aspect-[16/10] overflow-hidden"><img loading="lazy" decoding="async" src={p.image} alt={p.image_alt || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-5">
                    <span className="inline-block bg-orange/10 text-orange text-xs font-bold px-2.5 py-0.5 rounded-full mb-2">{p.category}</span>
                    <h3 className="font-display font-bold text-navy leading-snug group-hover:text-orange transition-colors line-clamp-2">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {matchGuide && matchGuide.file_url && !ctaDismissed && (
        <div className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-40 sm:max-w-sm" data-testid="blog-sticky-guide-cta">
          <div className="bg-navy text-white rounded-2xl shadow-2xl p-4 pr-9 flex items-center gap-3 border border-white/10 relative">
            <button onClick={() => setCtaDismissed(true)} aria-label="Dismiss" data-testid="blog-sticky-guide-dismiss" className="absolute top-2 right-2 text-slate-400 hover:text-white"><X size={15} /></button>
            <div className="w-10 h-10 rounded-lg bg-orange/20 flex items-center justify-center shrink-0"><Download size={18} className="text-orange" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-orange font-bold">Free guide</p>
              <p className="text-sm font-semibold truncate">{matchGuide.title}</p>
            </div>
            <button onClick={() => setActiveGuide(matchGuide)} data-testid="blog-sticky-guide-download" className="shrink-0 bg-orange hover:bg-orange-dark text-white text-sm font-bold px-4 py-2 rounded-full transition-colors">Download</button>
          </div>
        </div>
      )}
      <GuideDownloadModal guide={activeGuide} open={!!activeGuide} onClose={() => setActiveGuide(null)} />
    </div>
  );
}
